import React, { useState, useEffect, useContext } from "react";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import styled from "styled-components";
import { toObjectLiteral } from "common/utils";
import { useSearchParams } from "react-router-dom";
import TimeEvolutionMetricsResult from "./Result";
import useDebouncer from "common/hooks/debouncer";
import Radio from "common/Radio.jsx";
import ActionsContext from "common/ActionsContext";
import { periodChoices } from "common/constants";

const ControlsAndResultsLayout = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 10px 20px 20px;
`;

const ReloadButton = styled.div`
  grid-column: -2 / span 1;
  text-align: right;
  cursor: pointer;
`;

const IconRefresh = styled.i`
  color: #999;
  &:hover {
    color: #000;
  }
  transition: all 0.5s;
`;

const ResultsLayout = styled.div`
  padding: 0 25px 0 20px;
  width: 100%;
`;

const MetricsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
`;

const RadioOptionWrapper = styled.div`
  display: flex;
`;

const MetricsLabel = styled.div`
  width: 35px;
  display: flex;
  align-items: center;

  .material-icons {
    font-size: 20px;
  }
`;

const MetricOptionsWrapper = styled.div`
  width: 100%;
`;

const metricOptions = [
  { label: "Average Speed", value: "averageSpeed" },
  { label: "Maximum Speed", value: "maxSpeed" },
  { label: "Latency-P80", value: "latency-80p" },
  { label: "Latency-Median", value: "latency-med" },
  { label: "Latency-Mean-Min", value: "latency" },
  { label: "Retransmissions", value: "retransmissions" },
  { label: "Active Flows", value: "activeFlows" },
  { label: "Flows Created Per Minute", value: "flowsCreated" },
  { label: "% Traffic at Max Speed", value: "traffic" },
  { label: "% Traffic in Congestion", value: "congestion" },
];

const entityOptions = [
  { label: "IP Address", value: "ip-address" },
  { label: "Subscriber ID", value: "subscriber-id" },
];

const TimeEvolutionMetrics = ({
  direction,
  subscribersToPlot,
  timePeriod,
  setTimePeriod,
  mode,
  bqnId,
  isFirstSelection = true,
  selMetric,
  setSelMetric,
  entity,
  setEntity,
  meanSorting,
  setMeanSorting,
  setFieldsMeanMap,
}) => {
  const actions = useContext(ActionsContext);
  const [selected, setSelected] = useState([]);
  let [searchParams, setSearchParams] = useSearchParams();
  const debounce = useDebouncer(2500 /*ms*/);

  useEffect(() => {
    if (isFirstSelection) {
      setSelected(subscribersToPlot);
    } else {
      debounce(() => {
        setSelected(subscribersToPlot);
      });
    }
  }, [subscribersToPlot.map(s => s.id).join(","), isFirstSelection]);

  return (
    <div className="card attached">
      <ControlsAndResultsLayout>
        <ColumnsGrid columns={6} minWidth="18rem" rowGap="0">
          <SelectInputWithIcon
            title="Time Period"
            name="timePeriod"
            icon="date_range"
            selected={timePeriod}
            onChange={({ target }) => {
              setTimePeriod(target.value)
              setSearchParams({
                ...toObjectLiteral(searchParams),
                timePeriod: target.value,
              });
            }}
            options={periodChoices}
          />
          <SelectInputWithIcon
            title="entity"
            name="entity"
            icon="person"
            selected={entity}
            onChange={({ target }) => setEntity(target.value)}
            options={entityOptions}
          />
          <ReloadButton
            title="Refresh"
            className="context-menu header-dropdown m-r--5 button-reload-subs-groups-table"
            onClick={() => {
              actions.send("do-load");
            }}
          >
            <IconRefresh className="material-icons">refresh</IconRefresh>
          </ReloadButton>
        </ColumnsGrid>
        <MetricsWrapper>
          <MetricsLabel>
            <i className="material-icons">insert_chart</i>
          </MetricsLabel>
          <MetricOptionsWrapper>
            <ColumnsGrid columns={6} minWidth="18rem" rowGap="0">
              <Radio
                options={metricOptions}
                name="metric"
                defaultValue={selMetric}
                itemWrapper={RadioOptionWrapper}
                onChange={(event) => setSelMetric(event.target.value)}
              />
            </ColumnsGrid>
          </MetricOptionsWrapper>
        </MetricsWrapper>
      </ControlsAndResultsLayout>
      <ResultsLayout>
        <TimeEvolutionMetricsResult
          direction={direction}
          timePeriod={timePeriod}
          metric={selMetric}
          entity={entity}
          subscribersToPlot={selected}
          mode={mode}
          bqnId={bqnId}
          meanSorting={meanSorting}
          setMeanSorting={setMeanSorting}
          setFieldsMeanMap={setFieldsMeanMap}
        />
      </ResultsLayout>
    </div>
  );
};

export default TimeEvolutionMetrics;
